import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { recurrenceMetadataForEvent } from './recurrence.js'
import { cacheCalendarEventRsvp, calendarParticipantRsvpKeys } from './calendarUtils.js'

// SDK entity path — the workspace fetch plugin is wired to `{ adapter: 'sdk' }`,
// so `from` must be a dotted entity registered in EntityDispatcher (not a raw
// raw table name). New events use `create` (insert); existing events with a
// real DB id use `update`. This mirrors saveTicket.js which had the
// same upsert-rejection issue (#2204 — fetch.upsert dispatched to a route the
// EntityDispatcher rejected before upsertEvent shipped, causing silent data
// loss). Branching on optimistic id guarantees the right HTTP verb and avoids
// any post-conflict ambiguity in the workspace PostgREST passthrough.
const CALENDAR_ENTITY = 'workspaceProject.calendar'
// Date.now() is 13 digits in 2026; legacy autoincrement ids are <13 digits
// for the foreseeable future. Anything ≥ this is an optimistic local id.
const OPTIMISTIC_ID_FLOOR = 1_000_000_000_000
// Upper bound of the optimistic band (16 digits). Without it a long all-digit
// server id would read as optimistic — a Mongo ObjectId is 24 hex chars and may
// legally be all digits. Kept identical to the shell's rowOps.js classifier
// (WS-ROWOPS-OPTIMISTIC-ID-STRING-1) so both callers agree.
const OPTIMISTIC_ID_CEILING = 1_000_000_000_000_000
const _isOptimisticNumber = (n) =>
  Number.isFinite(n) && n >= OPTIMISTIC_ID_FLOOR && n < OPTIMISTIC_ID_CEILING
// An id is optimistic ONLY when it has the CLIENT's own optimistic shape.
// Every optimistic id this package mints is `Date.now()` (QuickCreate
// _saveQuickCreate, NewBookingModal's create branch + its drag proposal,
// pages/index.js's drag proposal) — a number at or above the floor.
//
// WS-CALENDAR-SAVEEVENT-OPTIMISTIC-ID-DUPES-1: the old test was
// `typeof id !== 'number' || id >= FLOOR`. Post-cutover the server exposes a
// 24-hex ObjectId STRING id (server src/domains/calendar/store/ids.js
// EXPOSED-ID RULE), so "not a number" stopped meaning "optimistic": an UPDATE
// by a real string id was classified optimistic and ran as a CREATE, so one
// New Booking landed TWO rows (and deleteEvent silently no-op'd on a string
// id). A server-exposed id — string or legacy number below the floor — is
// PERSISTED. Only an absent id, or a numeric (or numeric-string) id at/above
// the floor, is the client's own optimistic id.
const isOptimisticId = (id) => {
  if (id == null || id === '') return true
  if (typeof id === 'number') return Number.isFinite(id) ? _isOptimisticNumber(id) : true
  if (typeof id === 'string') {
    const trimmed = id.trim()
    // A `Date.now()` id that arrived stringified is still optimistic; any
    // other string (24-hex ObjectId, uuid, …) is a real server-exposed id.
    if (!/^\d+$/.test(trimmed)) return false
    return _isOptimisticNumber(Number(trimmed))
  }
  return true
}
const nowIso = () => new Date().toISOString()

// `date` + `startTime` are local wall-clock (the user dragged "8:30 on Apr 9"
// in their own timezone). Resolve them into a UTC instant via `new Date(...)`,
// which interprets the literal as local. This makes start_at the canonical
// timestamp the rest of the pipeline (Google sync push, _rowToEvent) reads.
const _localToUtcIso = (date, time) => {
  if (!date || !time) return null
  const d = new Date(`${date}T${time}:00`)
  return isNaN(d) ? null : d.toISOString()
}

// Keep an existing canonical timestamp only while it still represents the
// event's current wall-clock fields. Dragging an event changes date/time on
// the optimistic event but deliberately preserves its other fields, including
// startAt/endAt. Trusting those stale instants made the move look successful
// until refresh, when rowToEvent correctly rebuilt the old date from start_at.
const _canonicalInstant = (stored, date, time) => {
  const next = _localToUtcIso(date, time)
  if (!stored || !next) return next
  const storedMs = new Date(stored).getTime()
  const nextMs = new Date(next).getTime()
  return Number.isFinite(storedMs) && storedMs === nextMs ? stored : next
}

// Add one calendar day to a 'YYYY-MM-DD' string. Used to compute end_date
// for all-day events (Google represents "1 day all-day" as start.date=X,
// end.date=X+1 — a half-open interval).
const _addDay = (ymd) => {
  if (!ymd) return null
  const [y, m, d] = ymd.split('-').map(Number)
  const next = new Date(y, m - 1, d + 1)
  const pad = (n) => String(n).padStart(2, '0')
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`
}

// Carry-over-only fields — color and the two Google-sync ids. End date is
// managed by NewBookingModal for timed multi-day spans. Every EXISTING caller
// reaches the remaining fields by spreading the full
// original row (`{ ...ev, ...payload }`) before calling saveEvent — `ev.*`
// is always the real persisted value there. NewBookingModal's off-route edit
// fallback (SaveBtn.onClick, original missing from `root.events`) can't do
// that spread — it builds a bare `{ id, ...payload }` where these keys are
// simply absent (`undefined`). Treat "caller didn't provide this at all" as
// "leave the existing DB value alone" (omit from the UPDATE) rather than
// defaulting to `null` — the old unconditional `ev.field || null` shape
// would otherwise silently sever an event's Google Calendar sync link (or
// collapse a multi-day span to one day) the first time it's edited from
// off /calendar. Every existing caller already passes these fields defined
// (real value or explicit null), so this is a no-op for them.
const _toRow = (ev, currentUserEmail) => {
  const allDay = !!ev.allDay
  const endDate = allDay ? _addDay(ev.date) : ev.endDate || null
  const integrationMetadata = recurrenceMetadataForEvent(ev)
  return {
    ...(typeof ev.id === 'number' && !isOptimisticId(ev.id) ? { id: ev.id } : {}),
    title: ev.title || '(no title)',
    date: ev.date,
    // All-day events always derive end_date from date (Google's half-open-
    // interval representation) — that direction is unconditional. A timed
    // event's end_date only differs from `date` on a genuine multi-day span;
    // see the carry-over comment above for why this is guarded.
    ...(allDay || ev.endDate !== undefined ? { end_date: endDate } : {}),
    // All-day events leave start_time/end_time NULL — the sync server's
    // pushUser keys off `!start_time` to send Google's `start: { date }`
    // shape instead of `dateTime`.
    start_time: allDay ? null : ev.startTime || null,
    end_time: allDay ? null : ev.endTime || null,
    // Preserve original UTC timestamps when available (editing without time change).
    // Re-convert only when missing (new event) or times were actually modified.
    start_at: _canonicalInstant(ev.startAt, ev.date, allDay ? '00:00' : ev.startTime),
    end_at: _canonicalInstant(
      ev.endAt,
      allDay ? endDate : ev.endDate || ev.date,
      allDay ? '00:00' : ev.endTime
    ),
    ...(ev.color !== undefined ? { color: ev.color || null } : {}),
    type: ev.type || 'meeting',
    description: ev.description || null,
    invitees: ev.invitees || [],
    organizer_email: ev.creatorEmail || currentUserEmail,
    ...(ev.googleEventId !== undefined ? { google_event_id: ev.googleEventId || null } : {}),
    ...(ev.googleCalendarId !== undefined
      ? { google_calendar_id: ev.googleCalendarId || null }
      : {}),
    // Reminder minutes (#2200). Migration 0110 added
    // calendar_events.reminder_minutes to the schema. Omit when null so that
    // events without a reminder don't require the column to be writable — a
    // safe no-op against older DB snapshots and avoids a NOT NULL constraint
    // violation if the column is ever made non-nullable in the future.
    ...(ev.reminderMinutes != null ? { reminder_minutes: Number(ev.reminderMinutes) } : {}),
    ...(integrationMetadata !== undefined ? { integration_metadata: integrationMetadata } : {}),
    updated_at: nowIso(),
    deleted: false,
    dirty: true
  }
}

// Fire-and-forget — the UI shouldn't block on the Google round-trip (the
// server enqueues a pass per cursor and returns immediately).
//
// This used to POST the retired `calendar-sync` edge function; the worker route
// now answers 410 — so behind the `.catch(() => {})` every resync had become a
// silent no-op. Google sync is Mongo-native on the main server now:
// POST /core/calendar/sync (tickets/server.md "calendar Google-sync rehome").
// `el` is the calling element; callers inside this module (all already
// `this`-bound via their own el.call contract) pass it explicitly as
// `triggerSync(this)`. Kept as a real parameter rather than an implicit
// `.call(this)` this-binding on purpose: frank's globalScope lift (this
// helper is referenced as a free var from saveEvent/deleteEvent/
// updateSharedEventFields, none of which export it under its bare name)
// only treats `this` as the element ref for CALL_INVOKED_SECTIONS
// (functions/methods bags called positionally via el.call); a private,
// zero-param, globalScope-lifted helper falls through to frank's generic
// "no params → inject el" branch instead, which left a bare `el` free
// identifier with nothing to resolve it to once the sync npm-module
// preamble below started reading `el?.context`. See tickets/fable.md
// "workspace-calendar frank serialization blockers".
const triggerSync = async function (el) {
  try {
    const sdk = getSDK()
    // sdk._context.activeOrgId is the live source — verified in the browser.
    // sdk._currentUser is NOT populated in the workspace shell (it stays null
    // after boot), so resolving from it silently yielded no org and skipped
    // the sync entirely.
    const root = el?.state?.root
    const organization =
      sdk?._context?.activeOrgId || root?.user?.activeOrganization || root?.org?.id
    if (!organization) {
      _logErr('triggerCalendarSync', 'no active organization — sync skipped')
      return
    }
    // The TAB's workspace: the same live SDK context (the scope the calendar
    // write itself was stamped with), then the tab's resolved record. A sync
    // that names no workspace is scoped by the server to the caller's STORED
    // activeWorkspace, which another tab or session can have moved to another
    // org: "workspace does not belong to this organization" (dev 2026-10-29,
    // after an AI Undo of an event). No workspace → the old `{ organization }`
    // body.
    const workspaceId =
      sdk?._context?.activeWorkspaceId || root?.workspace?.id || root?.workspace?._id
    await sdk.calendarSync({ organization, ...(workspaceId ? { workspaceId } : {}) })
  } catch (err) {
    // Still non-fatal: the save itself already succeeded. Log it rather than
    // swallowing — a silent catch is what hid the dead edge function.
    _logErr('triggerCalendarSync', err)
  }
}

const _logErr = (scope, err) => console.error(`[${scope}]`, err?.message || err)

// SILENT-SAVE-FAIL-1 (tickets/opus.md) — every failure path in saveEvent used
// to end at `_logErr` + `return null`, and NOT ONE of its eight call sites
// checks the return value (most are a bare `el.call('saveEvent', ev)`; the two
// that do `.then(realId => ...)` only use it to patch an optimistic id). So a
// write that failed was indistinguishable from one that succeeded: the modal
// closed, the optimistic edit stayed on screen, and the original came back on
// the next reload with no error ever shown. The reported symptom — "change End
// 10:00→10:30, click Update, reload shows the original 09:00–10:00" — is that
// exactly.
//
// A console.error is not a report. Telling the user is the fix that
// generalises across all eight call sites without touching any of them.
// `titleKey`/`fallbackKey` are polyglot KEYS, resolved INSIDE the try below:
// a host with no `call` at all must still reach `_logErr`, which is the whole
// point of the catch (see the last test in saveEventSilentFail.test.js).
const _writeFailed = (el, { scope, titleKey, fallbackKey }, err, opts) => {
  if (!opts?.skipLog) _logErr(scope, err)
  // `hideDetail` keeps a machine-shaped reason (the refusal note below) out of
  // the notification body — the user reads the fallback copy, the console
  // keeps the diagnosis.
  const detail = opts?.hideDetail
    ? ''
    : (err && (err.message || err.error || err.hint)) || (typeof err === 'string' ? err : '')
  try {
    el.call('openNotification', {
      type: 'error',
      title: el.call('polyglot', titleKey),
      message: detail ? String(detail) : el.call('polyglot', fallbackKey)
    })
  } catch (e) {
    // A missing notification host must not swallow the original failure;
    // _logErr above already recorded it.
  }
}

const _saveFailed = (el, err, opts) =>
  _writeFailed(
    el,
    {
      scope: 'saveEvent',
      titleKey: 'calendar.save.failedTitle',
      fallbackKey: 'calendar.save.failedMessage'
    },
    err,
    opts
  )

const _deleteFailed = (el, err, opts) =>
  _writeFailed(
    el,
    {
      scope: 'deleteEvent',
      titleKey: 'calendar.delete.failedTitle',
      fallbackKey: 'calendar.delete.failedMessage'
    },
    err,
    opts
  )

// WS-CALENDAR-FETCH-UPDATE-NOOP-1 — a TARGETED write (the params carry the row
// id) that answers `data: []` wrote NOTHING. The server returns that empty
// shape on purpose for a row the caller cannot read, a row in another
// workspace and an id that does not exist; a row the caller CAN see answers an
// explicit 403 instead (server 7bb8e205), so 403-vs-[] is never an existence
// oracle. Either way the row is unchanged, so the client must read `[]` as a
// refusal — reading it as success is what made an edit and a soft-delete
// silent no-ops. A NON-targeted patch (updateSharedEventFields, matching by
// google_event_id or title+start_at) may legitimately match zero rows and is
// deliberately NOT checked here. A response with no `data` key at all is a
// body-less 200 (the write landed, nothing echoed) — also not a refusal.
const _refusedTargetedWrite = (result) => Array.isArray(result?.data) && result.data.length === 0

// `opts.falseOnFail`: a caller that must undo its own optimistic change on a
// failed write gets `false` from the five failure paths below. The default
// answer stays `null` for both a failure and a body-less success, so the call
// sites that read the answer as an id keep working unchanged. The calendar's
// delete (confirmEventDeletion) and a proposal Accept use the flag.
export const saveEvent = async function (ev, opts) {
  const fail = opts && opts.falseOnFail ? false : null
  if (isOptimisticId(ev?.id) && this.call?.('calendarBookingStartsInPast', ev) === true) {
    const root = this.state?.root
    if (root?.update && ev?.id != null) {
      root.update({ events: (root.events || []).filter((event) => event.id !== ev.id) })
    }
    _writeFailed(
      this,
      {
        scope: 'saveEvent',
        titleKey: 'calendar.save.pastTitle',
        fallbackKey: 'calendar.save.pastMessage'
      },
      null,
      { skipLog: true }
    )
    return fail
  }
  const fetch = this.context?.fetch
  if (!fetch?.insert || !fetch?.update) {
    _saveFailed(this, 'context.fetch missing insert/update')
    return fail
  }
  const row = _toRow(ev, this.state?.root?.currentUserEmail)
  try {
    let result
    if (isOptimisticId(ev.id)) {
      // New event — strip the local optimistic id before inserting so
      // PostgreSQL assigns the real autoincrement primary key.
      const { id, ...insertRow } = row
      result = await fetch.insert({ from: CALENDAR_ENTITY, data: insertRow })
    } else {
      // Existing event — update by real DB id.
      result = await fetch.update({ from: CALENDAR_ENTITY, params: { id: ev.id }, data: row })
    }
    if (result?.error) {
      _saveFailed(this, result.error)
      return fail
    }
    if (!isOptimisticId(ev.id) && _refusedTargetedWrite(result)) {
      _saveFailed(this, `update on id ${ev.id} matched no writable row (200 + [])`, {
        hideDetail: true
      })
      return fail
    }
    triggerSync(this)
    // `insert` returns data as a plain object (not an array) from the
    // workspace worker's /calendar/events POST response. `update` returns
    // the patched row similarly. Normalise both into a real numeric id —
    // resolved BEFORE the invite notifications below so they can carry it.
    const data = result?.data
    const savedId = (Array.isArray(data) ? data[0]?.id : data?.id) ?? null
    // An event saved from an AI answer's form (calendarAiFormOpen → the
    // booking's `aiRequest`): the answer shows the saved event as a row.
    if (ev.aiRequest && typeof this.context?.functions?.assistantFormSaved === 'function') {
      const savedRef = savedId ?? (isOptimisticId(ev.id) ? null : ev.id)
      this.call('assistantFormSaved', ev.aiRequest, {
        id: savedRef,
        title: ev.title || '',
        meta: [ev.date, ev.allDay ? '' : ev.startTime].filter(Boolean).join(' '),
        route: savedRef != null ? '/calendar?event=' + encodeURIComponent(savedRef) : '/calendar'
      })
    }
    // Notify invitees about the calendar event
    const me = this.state?.root?.currentUserEmail
    const invitees = ev.invitees || []
    if (invitees.length && me) {
      try {
        const { createNotifications } =
          await import('@symbo-ls/workspace-shared/functions/notifications/createNotification.js')
        // The update path can miss a row id in the response — the caller's
        // real id (never an optimistic one) backfills it.
        const eventId = savedId ?? (isOptimisticId(ev.id) ? null : ev.id)
        createNotifications.call(
          this,
          invitees.filter((e) => e !== me),
          {
            type: 'calendar_invite',
            title: `${ev.title || 'New event'} — you're invited`,
            body: ev.description?.substring(0, 100) || '',
            // Query-free href — survives the desktop-notification click path,
            // which assigns it straight to a pathname (same rationale as the
            // server's buildTicketHref). The event id rides `metadata` so the
            // in-app click paths can open CalEventDetailModal in place
            // (shared openNotificationTarget.js).
            href: '/calendar',
            ...(eventId != null ? { metadata: { eventId } } : {})
          }
        )
      } catch (e) {
        _logErr('saveEvent:notify', e)
      }
    }
    // NOTE: a missing `data` here is a SUCCESS with no response body (the
    // write landed; the worker just returned nothing to echo), not a failure —
    // so it returns null WITHOUT notifying. Only the three genuine failure
    // paths above and the throw below tell the user.
    if (!data) return null
    return savedId
  } catch (e) {
    _saveFailed(this, e)
    return fail
  }
}

// Soft-delete: flip `deleted` + `dirty` so the sync server can push the
// tombstone to Google Calendar before the row is physically removed.
//
// `opts.callerReports`: a caller that tells the person itself gets the
// failure THROWN and no toast: one failure, one message. The AI timeline's
// Undo (aiUndoCreated) is that caller. Its row already says "Undo did not
// work." and why (ChatToolRow UndoFailure), so the toast was a second message
// for the same failure, with the raw English detail (wave 2, dev 2026-10-28).
// The thrown error keeps its structured fields (the SDK error's `status` /
// `code`); a refused soft-delete (200 + []: no row this caller can see) is a
// 404, a missing fetch is DELETE_UNAVAILABLE. The caller logs it. Every other
// caller (the calendar's own delete) keeps the toast and the `false`.
export const deleteEvent = async function (eventId, opts) {
  if (isOptimisticId(eventId)) return true
  const el = this
  const failed = (err, fields, notify) => {
    if (opts && opts.callerReports) {
      throw fields ? Object.assign(new Error(String(err)), fields) : err
    }
    _deleteFailed(el, err, notify)
    return false
  }
  const fetch = this.context?.fetch
  if (!fetch?.update) {
    return failed(
      'context.fetch.update missing',
      { code: 'DELETE_UNAVAILABLE' },
      { hideDetail: true }
    )
  }
  let result
  try {
    result = await fetch.update({
      from: CALENDAR_ENTITY,
      params: { id: eventId },
      data: { deleted: true, dirty: true, updated_at: nowIso() }
    })
  } catch (e) {
    return failed(e)
  }
  if (result?.error) return failed(result.error)
  if (_refusedTargetedWrite(result)) {
    return failed(
      `soft-delete on id ${eventId} matched no writable row (200 + [])`,
      { status: 404 },
      { hideDetail: true }
    )
  }
  triggerSync(this)
  return true
}

// Update shared fields (type, description, invitees) on all rows with the same
// google_event_id. Called when editing an event that's shared across calendars.
// Update shared fields on all copies of the same event. Matches by
// google_event_id first, then also by title+start_at to catch forwarded
// copies that have a different Google event ID.
export const updateSharedEventFields = async function (ev, fields) {
  const fetch = this.context?.fetch
  if (!fetch?.update) return
  const data = { updated_at: nowIso(), dirty: true }
  if (fields.type !== undefined) data.type = fields.type
  if (fields.description !== undefined) data.description = fields.description
  if (fields.invitees !== undefined) data.invitees = fields.invitees
  try {
    // Update by google_event_id
    if (ev.googleEventId) {
      await fetch.update({
        from: CALENDAR_ENTITY,
        params: { google_event_id: ev.googleEventId, deleted: false },
        data
      })
    }
    // Also update by title + start_at to catch forwarded copies
    if (ev.title && ev.startAt) {
      await fetch.update({
        from: CALENDAR_ENTITY,
        params: { title: ev.title, start_at: ev.startAt, deleted: false },
        data
      })
    }
    triggerSync(this)
  } catch (e) {
    _logErr('updateSharedEventFields', e)
  }
}

const _googleSeriesBase = (id) => String(id || '').replace(/_\d{8}(?:T\d{6}Z)?$/, '')

export const setEventRsvp = async function (ev, status, scope = 'future') {
  const root = this.state?.root
  const fetch = this.context?.fetch
  const viewer = root?.people?.find((person) => person.email === root.currentUserEmail) || {
    email: root?.currentUserEmail
  }
  const keys = calendarParticipantRsvpKeys(viewer)
  if (!ev || !keys.length || !fetch?.update) return false

  const baseId = _googleSeriesBase(ev.googleEventId)
  const googleSeries = !!ev.googleEventId && baseId !== String(ev.googleEventId || '') ? baseId : ''
  const localSeries = ev.recurring && ev.recurring !== 'none'
  const recurring = !!googleSeries || localSeries
  const sameSeries = (candidate) => {
    if (candidate?.id === ev.id) return true
    return !!googleSeries && _googleSeriesBase(candidate?.googleEventId) === googleSeries
  }
  const inScope = (candidate) => {
    if (!sameSeries(candidate)) return false
    if (!recurring || localSeries || scope === 'this') return candidate?.id === ev.id
    return String(candidate?.date || '') >= String(ev.date || '')
  }
  const patchEvent = (candidate) => {
    if (!inScope(candidate)) return candidate
    const metadata = { ...(candidate.integrationMetadata || {}) }
    if (recurring && scope === 'this') {
      const overrides = { ...(metadata.calendarParticipantResponseOverrides || {}) }
      const occurrence = { ...(overrides[ev.date] || {}) }
      for (const key of keys) occurrence[key] = status
      overrides[ev.date] = occurrence
      metadata.calendarParticipantResponseOverrides = overrides
    } else if (recurring) {
      const changes = { ...(metadata.calendarParticipantResponseChanges || {}) }
      for (const key of keys) {
        const entries = (changes[key] || []).filter((change) => change?.from !== ev.date)
        changes[key] = [...entries, { from: ev.date, status }].sort((a, b) =>
          a.from.localeCompare(b.from)
        )
      }
      metadata.calendarParticipantResponseChanges = changes
    } else {
      const responses = { ...(metadata.calendarParticipantResponses || {}) }
      for (const key of keys) responses[key] = status
      metadata.calendarParticipantResponses = responses
    }
    return {
      ...candidate,
      integrationMetadata: metadata
    }
  }

  const previousEvents = root.events || []
  const previousSelectedEvent = root.selectedEvent
  const events = previousEvents.map(patchEvent)
  const selectedEvent = { ...patchEvent(ev) }
  cacheCalendarEventRsvp(selectedEvent, status, scope)
  root.update({
    events,
    selectedEvent
  })

  try {
    const metadata = selectedEvent.integrationMetadata
    const params =
      googleSeries && scope === 'future'
        ? {
            google_event_id: { like: `${googleSeries}%` },
            start_at: { gte: ev.startAt || `${ev.date}T00:00:00.000Z` },
            deleted: false
          }
        : { id: ev.id }
    const result = await fetch.update({
      from: CALENDAR_ENTITY,
      params,
      data: { integration_metadata: metadata, updated_at: nowIso() }
    })
    if (result?.error || ((!googleSeries || scope === 'this') && _refusedTargetedWrite(result))) {
      throw result?.error || new Error('RSVP update matched no writable event')
    }
    cacheCalendarEventRsvp(selectedEvent)
    return true
  } catch (error) {
    root.update({ events: previousEvents, selectedEvent: previousSelectedEvent })
    _saveFailed(this, error)
    return false
  }
}

export { triggerSync as triggerCalendarSync }
